<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useHead } from '#imports'
import { useAppearanceStore } from '@stores/appearance'
import { useAuthStore } from '@stores/auth'
import { useFavoritesStore } from '@stores/favorites'
import { useKeyControlsService } from '@composables/use-key-controls-service'
import { useMessages } from '@composables/use-messages'
import svgSprite from './components/svg-sprite'
import NotificationDialog from './components/notification-dialog'
import ModalManager from './components/modal-manager'
import Logo from './components/logo'
import CustomNativeSmartBanner from './partials/custom-native-smart-banner'
import AppInitializer from './services/app-initializer'
import appLoadTracker from './services/mixpanel/app-load-tracker'
import modalService from './services/modal-service'
import { isInAppBrowser } from './utils/user-agent'
import { pageKey } from './utils/page-key'
import { features } from './configuration'
import { siteUrl } from '@/config/site'
import { publicConfig } from '@/config/runtime'

const route = useRoute()
const router = useRouter()
const appearanceStore = useAppearanceStore()
const authStore = useAuthStore()
const favoritesStore = useFavoritesStore()
const getMessageText = useMessages()
const keyControlsService = useKeyControlsService()

const isLoading = ref(false)
const hasRouteInitialized = ref(false)

// The site-wide head. A view overrides any of these by emitting the same `key`.
// `og:url` is derived from the route rather than read off `window.location`, so it
// holds under SSR in Story 3.
const socialImage = siteUrl('/frisky-social-share-image.png')
const currentUrl = computed(() => siteUrl(route.fullPath))
const shouldNoindex = computed(() => route.matched.some(record => record.meta && record.meta.noindex))
const smartBannerContent = computed(() => {
    const appId = `app-id=${publicConfig().appleAppId}`
    if (route.path === '/' && !hasRouteInitialized.value) return appId
    return `${appId}, app-argument=${siteUrl(route.fullPath)}`
})

useHead(computed(() => ({
    // Every view's title is injected into this template.
    titleTemplate: title => (title ? `${title} | FRISKY` : 'FRISKY'),
    meta: [
        { property: 'og:title', key: 'og:title', content: 'FRISKY' },
        { property: 'og:type', key: 'og:type', content: 'website' },
        { property: 'og:url', key: 'og:url', content: currentUrl.value },
        { property: 'og:image', key: 'og:image', content: socialImage },
        { property: 'og:site_name', key: 'og:site_name', content: 'FRISKY' },
        { property: 'fb:app_id', key: 'fb:app_id', content: '126928420652882' },
        { property: 'og:locale', key: 'og:locale', content: 'en_US' },
        { name: 'twitter:card', key: 'twitter:card', content: 'summary_large_image' },
        { name: 'twitter:url', key: 'twitter:url', content: currentUrl.value },
        { name: 'twitter:site', key: 'twitter:site', content: '@friskyradio' },
        { name: 'twitter:creator', key: 'twitter:creator', content: '@friskyradio' },
        { name: 'twitter:image', key: 'twitter:image', content: socialImage },
        { name: 'apple-itunes-app', content: smartBannerContent.value },
        ...(shouldNoindex.value
            ? [{ name: 'robots', key: 'robots', content: 'noindex, nofollow' }]
            : []),
    ],
})))

let appInitializer = null

const loggedIn = computed(() => authStore.loggedIn)
const isNotificationOpen = computed(() => appearanceStore.isNotificationOpen)

const updateFavorites = () => {
    loggedIn.value && favoritesStore.init()
}

const initializeApp = async () => {
    appInitializer = new AppInitializer({
        router,
        messages: getMessageText,
        features,
    })
    await appInitializer.initialize()
}

watch(loggedIn, updateFavorites, { immediate: true })

onMounted(async () => {
    isLoading.value = true
    try {
        await initializeApp()
    } catch (error) {
        // initialize() is designed never to reject; this is the last line of
        // defense — a boot error must degrade, never strand the splash.
        console.error('App initialization failed:', error)
    } finally {
        isLoading.value = false
    }
    window.addEventListener('keydown', keyControlsService.reactToKey)
    // Mark app as ready for TTI tracking
    appLoadTracker.markReady()

    if (isInAppBrowser()) {
        modalService.show('IN_APP_BROWSER_NOTICE')
    }
})

onBeforeUnmount(() => {
    window.removeEventListener('keydown', keyControlsService.reactToKey)
    if (appInitializer) appInitializer.destroy()
})
</script>

<template>
    <div>
        <svg-sprite />
        <div
            v-if="isLoading"
            class="app-content-loader"
        >
            <Logo size="large" />
        </div>
        <NuxtLayout v-else>
            <NuxtPage :page-key="pageKey" />
        </NuxtLayout>
        <NotificationDialog v-if="isNotificationOpen" />
        <ModalManager />
        <CustomNativeSmartBanner/>
    </div>
</template>

<style lang="scss">
@use '@design' as *;

@import '@design/motion-globals';
@import '@design/normalize';
@import '@design/utilities';
@import 'nprogress/nprogress.css';

@include init;
@include fdsCssVariables;

*,
*::before,
*::after {
    box-sizing: border-box;
}

::-webkit-scrollbar {
    width: $size-scrollbar-width;
}

::-webkit-scrollbar-track {
    opacity: 0;
}

::-webkit-scrollbar-thumb {
    min-height: $s-xxl;
    background-color: $c-text-disabled;
    background-clip: padding-box;
    border: 4px solid transparent;
    border-radius: $radius-16;
}

/* stylelint-disable selector-max-type -- app root: global element resets */
html {
    scroll-behavior: smooth;

    @include tablet {
        // Keep the (styled, space-taking) window scrollbar present even when a modal locks the
        // body, so the page doesn't shift 16px sideways. `scrollbar-gutter: stable` doesn't reserve
        // room for a ::-webkit-scrollbar-styled bar in Chromium (measured in the browser pass).
        // Tablet up only: phones have overlay scrollbars, so there is nothing to reserve.
        overflow-y: scroll;
    }
}

body {
    height: 100vh;
    background: $c-body-bg;
}

// viewport-fit=cover (the viewport meta in nuxt.config.ts) stops iOS letterboxing the page between
// the side safe areas in landscape; inset every in-flow and sticky element instead (env() is 0 on
// desktop and in portrait). #app is Nuxt's mount point (`app.rootId` in nuxt.config.ts).
body > #app {
    padding-right: env(safe-area-inset-right);
    padding-left: env(safe-area-inset-left);
}

option {
    color: $c-white;
    background: $c-surface-raised;
}

// One focus ring everywhere (Colors §05): 2px white, keyboard focus only.
:focus-visible {
    outline: 2px solid $c-state-focus-ring;
    outline-offset: 2px;
}

:focus:not(:focus-visible) {
    outline: none;
}
/* stylelint-enable selector-max-type */

.app-content-loader {
    position: fixed;
    top: 0;
    left: 0;
    z-index: 1000;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
}

.app-loader-logo {
    animation: fade var(--motion-shimmer) infinite alternate var(--ease-standard);
}

// ===
// Vendor
// ===

#nprogress .bar {
    height: 2px;
    background: $c-accent-default;
    will-change: transform;
}

#nprogress .peg {
    display: none;
}
</style>
